/**
 * Eight Mansions (八宅) Feng Shui - Kua Number Calculator
 * 
 * This utility calculates personal Kua numbers and determines
 * auspicious/inauspicious directions based on the Ba Zhai system.
 */

/**
 * Chinese New Year dates (approximate - based on lunar calendar)
 * If born BEFORE this date, use the previous year for Kua calculation
 */
const CHINESE_NEW_YEAR_DATES = {
  1940: '1940-02-08', 1941: '1941-01-27', 1942: '1942-02-15', 1943: '1943-02-05', 1944: '1944-01-25',
  1945: '1945-02-13', 1946: '1946-02-02', 1947: '1947-01-22', 1948: '1948-02-10', 1949: '1949-01-29',
  1950: '1950-02-17', 1951: '1951-02-06', 1952: '1952-01-27', 1953: '1953-02-14', 1954: '1954-02-03',
  1955: '1955-01-24', 1956: '1956-02-12', 1957: '1957-01-31', 1958: '1958-02-18', 1959: '1959-02-08',
  1960: '1960-01-28', 1961: '1961-02-15', 1962: '1962-02-05', 1963: '1963-01-25', 1964: '1964-02-13',
  1965: '1965-02-02', 1966: '1966-01-21', 1967: '1967-02-09', 1968: '1968-01-30', 1969: '1969-02-17',
  1970: '1970-02-06', 1971: '1971-01-27', 1972: '1972-02-15', 1973: '1973-02-03', 1974: '1974-01-23',
  1975: '1975-02-11', 1976: '1976-01-31', 1977: '1977-02-18', 1978: '1978-02-07', 1979: '1979-01-28',
  1980: '1980-02-16', 1981: '1981-02-05', 1982: '1982-01-25', 1983: '1983-02-13', 1984: '1984-02-02',
  1985: '1985-02-20', 1986: '1986-02-09', 1987: '1987-01-29', 1988: '1988-02-17', 1989: '1989-02-06',
  1990: '1990-01-27', 1991: '1991-02-15', 1992: '1992-02-04', 1993: '1993-01-23', 1994: '1994-02-10',
  1995: '1995-01-31', 1996: '1996-02-19', 1997: '1997-02-07', 1998: '1998-01-28', 1999: '1999-02-16',
  2000: '2000-02-05', 2001: '2001-01-24', 2002: '2002-02-12', 2003: '2003-02-01', 2004: '2004-01-22',
  2005: '2005-02-09', 2006: '2006-01-29', 2007: '2007-02-18', 2008: '2008-02-07', 2009: '2009-01-26',
  2010: '2010-02-14', 2011: '2011-02-03', 2012: '2012-01-23', 2013: '2013-02-10', 2014: '2014-01-31',
  2015: '2015-02-19', 2016: '2016-02-08', 2017: '2017-01-28', 2018: '2018-02-16', 2019: '2019-02-05',
  2020: '2020-01-25', 2021: '2021-02-12', 2022: '2022-02-01', 2023: '2023-01-22', 2024: '2024-02-10',
  2025: '2025-01-29', 2026: '2026-02-17', 2027: '2027-02-06', 2028: '2028-01-26', 2029: '2029-02-13',
  2030: '2030-02-03',
};

/**
 * Get the Chinese year for a given birth date
 * If born before Chinese New Year, use the previous year
 * @param {string} birthDate - Date in YYYY-MM-DD format
 * @returns {number} Chinese year for Kua calculation
 */
export const getChineseYear = (birthDate) => {
  const date = new Date(birthDate);
  const year = date.getFullYear();
  
  // Get Chinese New Year date for this year
  const cnyDateStr = CHINESE_NEW_YEAR_DATES[year];
  
  if (cnyDateStr) {
    const cnyDate = new Date(cnyDateStr);
    // If birth date is before Chinese New Year, use previous year
    if (date < cnyDate) {
      return year - 1;
    }
  }
  
  return year;
};

/**
 * Calculate Kua Number from birth date and gender
 * @param {string} birthDate - Date in YYYY-MM-DD format
 * @param {string} gender - 'male' or 'female'
 * @returns {number} Kua number (1-9, excluding 5)
 */
export const calculateKuaNumber = (birthDate, gender) => {
  // Get the Chinese year (adjusted for Chinese New Year)
  const chineseYear = getChineseYear(birthDate);
  
  // Reduce birth year to single digit
  let yearSum = chineseYear.toString().split('').reduce((sum, digit) => sum + parseInt(digit), 0);
  while (yearSum > 9) {
    yearSum = yearSum.toString().split('').reduce((sum, digit) => sum + parseInt(digit), 0);
  }
  
  let kua;
  if (gender === 'male') {
    // For males: 11 - yearSum (if result is 5, use 2)
    kua = 11 - yearSum;
    if (kua > 9) kua = kua - 9;
    if (kua === 5) kua = 2;
  } else {
    // For females: 4 + yearSum (if result is 5, use 8)
    kua = 4 + yearSum;
    if (kua > 9) kua = kua - 9;
    if (kua === 5) kua = 8;
  }
  
  return kua;
};

/**
 * Determine if Kua belongs to East or West group
 * @param {number} kua - Kua number
 * @returns {string} 'east' or 'west'
 */
export const getKuaGroup = (kua) => {
  const eastGroup = [1, 3, 4, 9];
  return eastGroup.includes(kua) ? 'east' : 'west';
};

/**
 * Element colors for the 5 elements
 */
export const ELEMENT_COLORS = {
  Wood: '#22c55e',   // Green
  Fire: '#dc2626',   // Red
  Earth: '#92400e',  // Brown
  Water: '#2563eb',  // Blue
  Metal: '#6b7280',  // Grey
};

/**
 * Direction data with trigrams, elements, and life aspects
 */
export const DIRECTIONS = {
  N: { 
    name: 'North', 
    chinese: '北', 
    trigram: 'Kan', 
    trigramChinese: '坎', 
    element: 'Water', 
    elementChinese: '水',
    elementColor: ELEMENT_COLORS.Water,
    lifeAspect: 'Career',
    lifeAspectChinese: '事業'
  },
  NE: { 
    name: 'Northeast', 
    chinese: '東北', 
    trigram: 'Gen', 
    trigramChinese: '艮', 
    element: 'Earth', 
    elementChinese: '土',
    elementColor: ELEMENT_COLORS.Earth,
    lifeAspect: 'Education',
    lifeAspectChinese: '學業'
  },
  E: { 
    name: 'East', 
    chinese: '東', 
    trigram: 'Zhen', 
    trigramChinese: '震', 
    element: 'Wood', 
    elementChinese: '木',
    elementColor: ELEMENT_COLORS.Wood,
    lifeAspect: 'Health',
    lifeAspectChinese: '健康'
  },
  SE: { 
    name: 'Southeast', 
    chinese: '東南', 
    trigram: 'Xun', 
    trigramChinese: '巽', 
    element: 'Wood', 
    elementChinese: '木',
    elementColor: ELEMENT_COLORS.Wood,
    lifeAspect: 'Wealth',
    lifeAspectChinese: '財富'
  },
  S: { 
    name: 'South', 
    chinese: '南', 
    trigram: 'Li', 
    trigramChinese: '離', 
    element: 'Fire', 
    elementChinese: '火',
    elementColor: ELEMENT_COLORS.Fire,
    lifeAspect: 'Recognition',
    lifeAspectChinese: '名聲'
  },
  SW: { 
    name: 'Southwest', 
    chinese: '西南', 
    trigram: 'Kun', 
    trigramChinese: '坤', 
    element: 'Earth', 
    elementChinese: '土',
    elementColor: ELEMENT_COLORS.Earth,
    lifeAspect: 'Relationship',
    lifeAspectChinese: '感情'
  },
  W: { 
    name: 'West', 
    chinese: '西', 
    trigram: 'Dui', 
    trigramChinese: '兌', 
    element: 'Metal', 
    elementChinese: '金',
    elementColor: ELEMENT_COLORS.Metal,
    lifeAspect: 'Children',
    lifeAspectChinese: '子女'
  },
  NW: { 
    name: 'Northwest', 
    chinese: '西北', 
    trigram: 'Qian', 
    trigramChinese: '乾', 
    element: 'Metal', 
    elementChinese: '金',
    elementColor: ELEMENT_COLORS.Metal,
    lifeAspect: 'Mentors',
    lifeAspectChinese: '貴人'
  },
};

/**
 * Auspicious direction types (ranked from best to least)
 */
export const AUSPICIOUS_TYPES = {
  SHENG_QI: { name: 'Sheng Qi', chinese: '生氣', meaning: 'Wealth & Success', color: '#22c55e', rank: 1 },
  TIAN_YI: { name: 'Tian Yi', chinese: '天醫', meaning: 'Health', color: '#3b82f6', rank: 2 },
  YAN_NIAN: { name: 'Yan Nian', chinese: '延年', meaning: 'Relationships', color: '#f59e0b', rank: 3 },
  FU_WEI: { name: 'Fu Wei', chinese: '伏位', meaning: 'Stability', color: '#8b5cf6', rank: 4 },
};

/**
 * Inauspicious direction types (ranked from worst to least bad)
 */
export const INAUSPICIOUS_TYPES = {
  JUE_MING: { name: 'Jue Ming', chinese: '絕命', meaning: 'Total Loss', color: '#dc2626', rank: 1 },
  WU_GUI: { name: 'Wu Gui', chinese: '五鬼', meaning: 'Misfortune', color: '#f97316', rank: 2 },
  LIU_SHA: { name: 'Liu Sha', chinese: '六煞', meaning: 'Conflict', color: '#ec4899', rank: 3 },
  HUO_HAI: { name: 'Huo Hai', chinese: '禍害', meaning: 'Problems', color: '#a855f7', rank: 4 },
};

/**
 * Kua direction mappings
 * Maps each Kua number to their auspicious and inauspicious directions
 */
const KUA_DIRECTIONS = {
  1: {
    auspicious: {
      SE: 'SHENG_QI',  // Wealth
      E: 'TIAN_YI',   // Health
      S: 'YAN_NIAN',  // Relationships
      N: 'FU_WEI',    // Stability
    },
    inauspicious: {
      SW: 'JUE_MING', // Total Loss
      NW: 'WU_GUI',   // Misfortune
      NE: 'LIU_SHA',  // Conflict
      W: 'HUO_HAI',   // Problems
    }
  },
  2: {
    auspicious: {
      NE: 'SHENG_QI',
      W: 'TIAN_YI',
      NW: 'YAN_NIAN',
      SW: 'FU_WEI',
    },
    inauspicious: {
      S: 'JUE_MING',
      SE: 'WU_GUI',
      N: 'LIU_SHA',
      E: 'HUO_HAI',
    }
  },
  3: {
    auspicious: {
      S: 'SHENG_QI',
      N: 'TIAN_YI',
      SE: 'YAN_NIAN',
      E: 'FU_WEI',
    },
    inauspicious: {
      W: 'JUE_MING',
      NE: 'WU_GUI',
      SW: 'LIU_SHA',
      NW: 'HUO_HAI',
    }
  },
  4: {
    auspicious: {
      N: 'SHENG_QI',
      S: 'TIAN_YI',
      E: 'YAN_NIAN',
      SE: 'FU_WEI',
    },
    inauspicious: {
      NE: 'JUE_MING',
      W: 'WU_GUI',
      NW: 'LIU_SHA',
      SW: 'HUO_HAI',
    }
  },
  6: {
    auspicious: {
      W: 'SHENG_QI',
      NE: 'TIAN_YI',
      SW: 'YAN_NIAN',
      NW: 'FU_WEI',
    },
    inauspicious: {
      E: 'JUE_MING',
      S: 'WU_GUI',
      SE: 'LIU_SHA',
      N: 'HUO_HAI',
    }
  },
  7: {
    auspicious: {
      NW: 'SHENG_QI',
      SW: 'TIAN_YI',
      NE: 'YAN_NIAN',
      W: 'FU_WEI',
    },
    inauspicious: {
      SE: 'JUE_MING',
      N: 'WU_GUI',
      S: 'LIU_SHA',
      E: 'HUO_HAI',
    }
  },
  8: {
    auspicious: {
      SW: 'SHENG_QI',
      NW: 'TIAN_YI',
      W: 'YAN_NIAN',
      NE: 'FU_WEI',
    },
    inauspicious: {
      N: 'JUE_MING',
      E: 'WU_GUI',
      S: 'LIU_SHA',
      SE: 'HUO_HAI',
    }
  },
  9: {
    auspicious: {
      E: 'SHENG_QI',
      SE: 'TIAN_YI',
      N: 'YAN_NIAN',
      S: 'FU_WEI',
    },
    inauspicious: {
      NW: 'JUE_MING',
      SW: 'WU_GUI',
      W: 'LIU_SHA',
      NE: 'HUO_HAI',
    }
  },
};

/**
 * Get all directions with their auspicious/inauspicious status for a Kua number
 * @param {number} kua - Kua number
 * @returns {Object} Direction analysis
 */
export const getKuaDirections = (kua) => {
  const kuaData = KUA_DIRECTIONS[kua];
  if (!kuaData) return null;
  
  const result = {};
  
  // Process auspicious directions
  Object.entries(kuaData.auspicious).forEach(([dir, type]) => {
    result[dir] = {
      ...DIRECTIONS[dir],
      isAuspicious: true,
      type: AUSPICIOUS_TYPES[type],
    };
  });
  
  // Process inauspicious directions
  Object.entries(kuaData.inauspicious).forEach(([dir, type]) => {
    result[dir] = {
      ...DIRECTIONS[dir],
      isAuspicious: false,
      type: INAUSPICIOUS_TYPES[type],
    };
  });
  
  return result;
};

/**
 * Get the direction key from compass degrees
 * @param {number} degrees - Compass degrees (0-360)
 * @returns {string} Direction key (N, NE, E, etc.)
 */
export const getDirectionFromDegrees = (degrees) => {
  // Normalize degrees to 0-360
  const normalized = ((degrees % 360) + 360) % 360;
  
  // Each direction spans 45 degrees
  if (normalized >= 337.5 || normalized < 22.5) return 'N';
  if (normalized >= 22.5 && normalized < 67.5) return 'NE';
  if (normalized >= 67.5 && normalized < 112.5) return 'E';
  if (normalized >= 112.5 && normalized < 157.5) return 'SE';
  if (normalized >= 157.5 && normalized < 202.5) return 'S';
  if (normalized >= 202.5 && normalized < 247.5) return 'SW';
  if (normalized >= 247.5 && normalized < 292.5) return 'W';
  if (normalized >= 292.5 && normalized < 337.5) return 'NW';
  return 'N';
};

/**
 * Get the direction status message based on Kua and current facing direction
 * @param {number} kua - Kua number
 * @param {number} degrees - Current compass degrees
 * @returns {Object} Status with direction info and meaning
 */
export const getDirectionStatus = (kua, degrees) => {
  const directions = getKuaDirections(kua);
  if (!directions) return null;
  
  const currentDir = getDirectionFromDegrees(degrees);
  const dirInfo = directions[currentDir];
  
  return {
    degrees,
    directionKey: currentDir,
    directionName: dirInfo.name,
    directionChinese: dirInfo.chinese,
    isAuspicious: dirInfo.isAuspicious,
    type: dirInfo.type,
    element: dirInfo.element,
    trigram: dirInfo.trigram,
  };
};

/**
 * Map 24 Mountains index to 8 direction key
 * @param {number} index - 24 Mountains index (0-23)
 * @returns {string} Direction key (N, NE, E, etc.)
 */
export const get8DirectionFrom24Mountains = (index) => {
  // Each direction spans 3 mountains (45 degrees / 15 degrees per mountain = 3)
  // Index 0 starts at N1 (352.5-7.5 degrees)
  
  // Mapping: indices 0,1,2 = N; 3,4,5 = NE; etc.
  const directionMap = ['N', 'N', 'N', 'NE', 'NE', 'NE', 'E', 'E', 'E', 'SE', 'SE', 'SE', 'S', 'S', 'S', 'SW', 'SW', 'SW', 'W', 'W', 'W', 'NW', 'NW', 'NW'];
  return directionMap[index] || 'N';
};
